import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, Trash2, Copy, Bot } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIDE = {
  firstname: "", contact: "", age: 25, gender: "Femme",
  city: "Abidjan", category: "lago", password: "", nb_annonces: 2,
  // Volet Love des profils d'attraction
  taille: "", silhouette: "", recherche: "", bio: "", interets: [],
  photo_visibilite: "visible",
};

export const MockAccountsSection = ({ adminId }) => {
  const [form, setForm] = useState(VIDE);
  const [refs, setRefs] = useState(null);
  const [comptes, setComptes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dernier, setDernier] = useState(null);

  const charger = useCallback(() => {
    axios.get(`${API}/admin/comptes-mock`, { params: { admin_id: adminId } })
      .then((r) => setComptes(r.data)).catch(() => {});
  }, [adminId]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    axios.get(`${API}/love/referentiel`).then((r) => setRefs(r.data)).catch(() => {});
  }, []);

  const basculerInteret = (i) =>
    setForm((f) => ({
      ...f,
      interets: f.interets.includes(i)
        ? f.interets.filter((x) => x !== i)
        : [...f.interets, i].slice(0, 8),
    }));

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const creer = async (e) => {
    e.preventDefault();
    if (!form.firstname.trim() || !form.contact.trim()) {
      toast.error("Nom complet et email/téléphone sont obligatoires.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/comptes-mock`, {
        admin_id: adminId, ...form, age: Number(form.age), nb_annonces: Number(form.nb_annonces),
      });
      setDernier(data);
      toast.success(`Compte "${data.firstname}" créé avec ${data.nb_annonces} annonce(s).`);
      setForm(VIDE);
      charger();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création.");
    } finally {
      setBusy(false);
    }
  };

  const supprimer = async (id) => {
    try {
      await axios.delete(`${API}/admin/comptes-mock/${id}`, { params: { admin_id: adminId } });
      toast.success("Compte mock supprimé.");
      charger();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const champ = "w-full rounded-xl border border-[#E5DFD5] bg-white px-3 py-2 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#E05638]/40";

  return (
    <section className="mt-14" data-testid="admin-mock-section">
      <h2 className="font-heading text-2xl font-extrabold text-stone-900 flex items-center gap-2.5">
        <Bot size={24} className="text-[#0A5C36]" /> Comptes mock (sans paiement)
      </h2>
      <p className="text-sm text-stone-500 mt-1">Créez des comptes de démonstration — abonnement actif + annonces, affichés comme de vrais membres.</p>

      <form onSubmit={creer} className="mt-6 bg-white border border-[#E5DFD5] rounded-2xl p-5 grid sm:grid-cols-2 gap-3">
        <input data-testid="mock-firstname" className={champ} placeholder="Nom complet (ex: Jean Mock)" value={form.firstname} onChange={set("firstname")} />
        <input data-testid="mock-contact" className={champ} placeholder="Email ou téléphone (+225…)" value={form.contact} onChange={set("contact")} />
        <input data-testid="mock-age" type="number" min="18" max="99" className={champ} placeholder="Âge" value={form.age} onChange={set("age")} />
        <select data-testid="mock-gender" className={champ} value={form.gender} onChange={set("gender")}>
          <option>Femme</option><option>Homme</option>
        </select>
        <input data-testid="mock-city" className={champ} placeholder="Ville (ex: Abidjan)" value={form.city} onChange={set("city")} />
        <select data-testid="mock-category" className={champ} value={form.category} onChange={set("category")}>
          <option value="lago">FodindinLago</option>
          <option value="love">FodindinLove</option>
          <option value="vip">FodindinClubVIP</option>
        </select>
        <input data-testid="mock-password" className={champ} placeholder="Mot de passe (vide = auto)" value={form.password} onChange={set("password")} />
        <select data-testid="mock-nb-annonces" className={champ} value={form.nb_annonces} onChange={set("nb_annonces")}>
          {[1, 2, 3, 4, 5].map((n) => (
            <option key={n} value={n}>{`${n} annonce${n > 1 ? "s" : ""}`}</option>
          ))}
        </select>
        {form.category === "love" && (
          <div className="sm:col-span-2 rounded-2xl border border-[#9f2558]/25 bg-[#9f2558]/5 p-4"
            data-testid="mock-love-bloc">
            <p className="text-xs font-bold uppercase tracking-wider text-[#9f2558]">
              Profil d'attraction FodindinLove
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <input data-testid="mock-love-taille" className={champ} placeholder="Taille (ex : 1m68)"
                value={form.taille} onChange={set("taille")} />
              <select data-testid="mock-love-silhouette" className={champ} value={form.silhouette} onChange={set("silhouette")}>
                <option value="">Silhouette…</option>
                {(refs?.silhouettes || []).map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <select data-testid="mock-love-recherche" className={`${champ} sm:col-span-2`} value={form.recherche} onChange={set("recherche")}>
                <option value="">Type de relation recherché…</option>
                {(refs?.recherches || []).map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <textarea data-testid="mock-love-bio" rows={2} className={`${champ} sm:col-span-2`}
                placeholder="Bio affichée sur la carte Love (500 caractères max)"
                value={form.bio} onChange={set("bio")} />
              <select data-testid="mock-love-visibilite" className={champ} value={form.photo_visibilite} onChange={set("photo_visibilite")}>
                {(refs?.visibilites || ["visible", "floutee", "anonyme"]).map((v) => (
                  <option key={v} value={v}>{v === "visible" ? "Photo visible" : v === "floutee" ? "Photo floutée" : "Anonyme"}</option>
                ))}
              </select>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="mock-love-interets">
              {(refs?.interets || []).map((i) => (
                <button key={i} type="button" onClick={() => basculerInteret(i)}
                  data-testid={`mock-love-interet-${i}`}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                    form.interets.includes(i)
                      ? "bg-[#9f2558] text-white"
                      : "bg-white text-stone-600 border border-[#E5DFD5]"}`}>
                  {i}
                </button>
              ))}
            </div>
          </div>
        )}
        <button type="submit" disabled={busy} data-testid="mock-submit"
          className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-[#E05638] hover:bg-[#c9452a] disabled:opacity-60 text-white font-semibold rounded-full py-2.5 transition-colors">
          <UserPlus size={16} /> {busy ? "Création…" : "Créer le compte mock"}
        </button>
      </form>

      {dernier && (
        <div className="mt-3 bg-[#0A5C36]/8 border border-[#0A5C36]/25 rounded-xl p-3 text-sm text-stone-700 flex items-center justify-between gap-3" data-testid="mock-last-created">
          <span>Identifiants — <b>{dernier.contact}</b> · mot de passe : <b className="font-mono">{dernier.password}</b></span>
          <button onClick={() => { navigator.clipboard?.writeText(`${dernier.contact} / ${dernier.password}`); toast.success("Copié"); }}
            className="shrink-0 text-[#0A5C36]" title="Copier"><Copy size={16} /></button>
        </div>
      )}

      <div className="mt-6 space-y-2" data-testid="mock-list">
        {comptes.length === 0 ? (
          <p className="text-sm text-stone-400">Aucun compte mock pour l'instant.</p>
        ) : comptes.map((c) => (
          <div key={c.id} data-testid={`mock-item-${c.id}`} className="flex items-center justify-between bg-white border border-[#E5DFD5] rounded-xl px-4 py-2.5 text-sm">
            <span className="truncate">
              <b className="text-stone-900">{c.firstname}</b>
              <span className="text-stone-400"> · {c.contact} · {c.category} · {c.city} · {c.nb_annonces} annonce(s)</span>
            </span>
            <span className="flex shrink-0 items-center gap-3">
              {c.category === "love" && (
                <a href={`/love/mon-profil?profile_id=${c.id}`} data-testid={`mock-love-editer-${c.id}`}
                  className="text-xs font-bold text-[#9f2558] hover:underline">
                  Infos Love
                </a>
              )}
              <button onClick={() => supprimer(c.id)} data-testid={`mock-delete-${c.id}`} className="text-red-500 hover:text-red-700" title="Supprimer">
                <Trash2 size={16} />
              </button>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};
